import type { Dayjs } from 'dayjs';
import { PMS_SETTINGS_OFFSET_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import { overlaps } from 'lumen/utils/dates/moment-helpers';
import safeMultiply from 'lumen/utils/numeric/safe-multiply';

// A minimal seasonal-strategy shape, satisfied by RecommendationStrategy, so the
// overlap helpers stay pure and don't depend on EmberData.
export type SeasonalStrategyDateRange = {
  seasonEndDt: Dayjs | null;
  seasonStartDt: Dayjs | null;
};

// Relative room offsets are stored as fractions (0.1) but shown as percentages
// (10). Absolute offsets pass through unchanged.
export function offsetToDisplayValue(
  value: number,
  offsetType: string,
): number {
  if (offsetType !== PMS_SETTINGS_OFFSET_TYPES.RELATIVE) {
    return value;
  }

  return safeMultiply(value, 100) ?? value;
}

// Inverse of offsetToDisplayValue: turns a displayed percentage (10) back into
// the stored fraction (0.1). Absolute offsets pass through unchanged.
export function offsetToStoredValue(value: number, offsetType: string): number {
  if (offsetType !== PMS_SETTINGS_OFFSET_TYPES.RELATIVE) {
    return value;
  }

  return safeMultiply(value, 1 / 100) ?? value;
}

// Returns the seasonal strategies whose date range overlaps [start, end].
// Comparison is inclusive by day, so strategies that touch on the same day still
// conflict. Strategies without a full date range can't overlap and are skipped.
// Callers should exclude the strategy being edited from `seasonalStrategies`.
export function getOverlappingSeasonalStrategies<
  T extends SeasonalStrategyDateRange,
>(start: Dayjs | null, end: Dayjs | null, seasonalStrategies: T[]): T[] {
  if (!start || !end) {
    return [];
  }

  return seasonalStrategies.filter(({ seasonEndDt, seasonStartDt }) => {
    if (!seasonStartDt || !seasonEndDt) {
      return false;
    }

    return overlaps(start, end, seasonStartDt, seasonEndDt);
  });
}
